import { DomainError } from './errors'
import { parseLocalDate } from './time'

/**
 * W2-S S1: the rate calendar's rules. One cell per category and Rome date
 * (migration 0051): a price in integer cents, the minimum days for a rental
 * that starts that day and for any rental through it, and stop sale. A null
 * is "nothing set": S2 refuses a day with no price, never prices it at 0.
 * Pure: the server reads and writes the cells; this says what a change does.
 */

/** A bulk change covers at most this many days, from its first to its last. */
export const DAY_RATES_MAX_BULK_DAYS = 400
/** The longest minimum a day can ask: two months, the season's longest stay. */
export const DAY_RATES_MAX_MIN_DAYS = 60
/** The highest price a day can have, €5,000: a guard against a slipped digit. */
export const DAY_RATES_MAX_PRICE_CENTS = 500_000

/** What a day says: the cell's values, without who wrote it. */
export interface DayRateValues {
  priceCents: number | null
  minDaysPickup: number | null
  minDaysThrough: number | null
  stopSale: boolean
}

/** A stored cell, as the fingerprint and the diff read it. */
export interface DayRateCell extends DayRateValues {
  categoryId: string
  day: string
  version: number
}

export const EMPTY_DAY: DayRateValues = {
  priceCents: null,
  minDaysPickup: null,
  minDaysThrough: null,
  stopSale: false,
}

/** How a bulk change sets the price: to an amount, or up or down from each day's own. */
export type PriceChange = { to: number } | { plusCents: number } | { minusCents: number }

/** What a bulk change sets. A field left out stays as each day has it; null clears it. */
export interface DayRatesSet {
  price?: PriceChange
  minDaysPickup?: number | null
  minDaysThrough?: number | null
  stopSale?: boolean
}

export interface DayRatesBulk {
  categoryIds: readonly string[]
  from: string
  to: string
  /** ISO weekdays, Monday 1 to Sunday 7. */
  weekdays: readonly number[]
  set: DayRatesSet
}

/** The days a bulk change covers: from its first to its last, on the weekdays chosen. */
export function bulkDays(
  bulk: Pick<DayRatesBulk, 'from' | 'to' | 'weekdays'>,
  today: string,
): string[] {
  const from = parseLocalDate(bulk.from)
  const to = parseLocalDate(bulk.to)
  if (to.until(from).sign > 0) {
    throw new DomainError('invalid_range', 'the last day comes before the first', {
      from: bulk.from,
      to: bulk.to,
    })
  }
  const span = from.until(to, { largestUnit: 'day' }).days + 1
  if (span > DAY_RATES_MAX_BULK_DAYS) {
    throw new DomainError(
      'range_too_long',
      `a change covers ${DAY_RATES_MAX_BULK_DAYS} days at most`,
      {
        days: span,
        max: DAY_RATES_MAX_BULK_DAYS,
      },
    )
  }
  if (from.until(parseLocalDate(today)).sign > 0) {
    throw new DomainError('day_past', 'a day already past is not changed', {
      day: bulk.from,
      today,
    })
  }
  const weekdays = new Set(bulk.weekdays)
  if (
    weekdays.size === 0 ||
    [...weekdays].some((day) => !Number.isInteger(day) || day < 1 || day > 7)
  ) {
    throw new DomainError(
      'invalid_weekdays',
      'choose at least one weekday, 1 (Monday) to 7 (Sunday)',
      {
        weekdays: bulk.weekdays,
      },
    )
  }
  const days: string[] = []
  for (let day = from; day.until(to).sign >= 0; day = day.add({ days: 1 })) {
    if (weekdays.has(day.dayOfWeek)) days.push(day.toString())
  }
  return days
}

/** Why a day of a bulk change is left as it is. */
export type DayRateSkip = 'no_price'

/**
 * What a bulk change makes of one day. A price moved up or down needs a price
 * to move: a day without one is skipped, not priced from nothing. A price
 * moved to zero or below refuses the whole change.
 */
export function applyDayRatesSet(
  current: DayRateValues,
  set: DayRatesSet,
): { next: DayRateValues } | { skip: DayRateSkip } {
  let priceCents = current.priceCents
  if (set.price) {
    if ('to' in set.price) priceCents = set.price.to
    else {
      if (current.priceCents === null) return { skip: 'no_price' }
      priceCents =
        'plusCents' in set.price
          ? current.priceCents + set.price.plusCents
          : current.priceCents - set.price.minusCents
    }
    if (priceCents <= 0) {
      throw new DomainError('price_not_positive', 'a day’s price stays above zero', {
        from: current.priceCents,
        to: priceCents,
      })
    }
    if (priceCents > DAY_RATES_MAX_PRICE_CENTS) {
      throw new DomainError('price_too_high', 'a day’s price is €5,000 at most', {
        to: priceCents,
        max: DAY_RATES_MAX_PRICE_CENTS,
      })
    }
  }
  return {
    next: {
      priceCents,
      minDaysPickup: set.minDaysPickup === undefined ? current.minDaysPickup : set.minDaysPickup,
      minDaysThrough:
        set.minDaysThrough === undefined ? current.minDaysThrough : set.minDaysThrough,
      stopSale: set.stopSale ?? current.stopSale,
    },
  }
}

export const sameDayRates = (a: DayRateValues, b: DayRateValues): boolean =>
  a.priceCents === b.priceCents &&
  a.minDaysPickup === b.minDaysPickup &&
  a.minDaysThrough === b.minDaysThrough &&
  a.stopSale === b.stopSale

export interface DayRateChange {
  categoryId: string
  day: string
  before: DayRateValues
  after: DayRateValues
}

export interface DayRatesPlan {
  changes: DayRateChange[]
  unchanged: number
  skipped: { categoryId: string; day: string; reason: DayRateSkip }[]
}

/**
 * Every cell a bulk change touches, old and new, category by category and
 * day by day: what the preview lists and the apply writes. `cells` are the
 * stored cells in the scope; a missing one is an empty day.
 */
export function planDayRates(
  bulk: DayRatesBulk,
  cells: readonly DayRateCell[],
  today: string,
): DayRatesPlan {
  const days = bulkDays(bulk, today)
  const stored = new Map(cells.map((cell) => [`${cell.categoryId}|${cell.day}`, cell]))
  const plan: DayRatesPlan = { changes: [], unchanged: 0, skipped: [] }
  for (const categoryId of [...new Set(bulk.categoryIds)].sort()) {
    for (const day of days) {
      const before = valuesOf(stored.get(`${categoryId}|${day}`))
      const result = applyDayRatesSet(before, bulk.set)
      if ('skip' in result) plan.skipped.push({ categoryId, day, reason: result.skip })
      else if (sameDayRates(before, result.next)) plan.unchanged++
      else plan.changes.push({ categoryId, day, before, after: result.next })
    }
  }
  return plan
}

const valuesOf = (cell: DayRateValues | undefined): DayRateValues =>
  cell
    ? {
        priceCents: cell.priceCents,
        minDaysPickup: cell.minDaysPickup,
        minDaysThrough: cell.minDaysThrough,
        stopSale: cell.stopSale,
      }
    : EMPTY_DAY

/**
 * The text a preview's fingerprint is the hash of: every day in the scope,
 * category by category, with its values and version, a missing cell named as
 * such. Any write to the scope between the preview and the apply changes it.
 */
export function dayRatesScopeText(
  categoryIds: readonly string[],
  days: readonly string[],
  cells: readonly DayRateCell[],
): string {
  const stored = new Map(cells.map((cell) => [`${cell.categoryId}|${cell.day}`, cell]))
  const lines: string[] = []
  for (const categoryId of [...new Set(categoryIds)].sort()) {
    for (const day of days) {
      const cell = stored.get(`${categoryId}|${day}`)
      lines.push(
        cell
          ? `${categoryId}|${day}|v${cell.version}|${cell.priceCents ?? '-'}|${cell.minDaysPickup ?? '-'}|${cell.minDaysThrough ?? '-'}|${cell.stopSale ? 1 : 0}`
          : `${categoryId}|${day}|none`,
      )
    }
  }
  return lines.join('\n')
}

/** A run of consecutive days that changed the same way: what the audit row records. */
export interface DayRateRun {
  from: string
  to: string
  before: DayRateValues
  after: DayRateValues
}

/**
 * One category's changes as runs of consecutive days with the same change,
 * so a season set at once is one line in the audit log, not ninety.
 */
export function dayRateRuns(changes: readonly Omit<DayRateChange, 'categoryId'>[]): DayRateRun[] {
  const sorted = [...changes].sort((a, b) => a.day.localeCompare(b.day))
  const runs: DayRateRun[] = []
  for (const change of sorted) {
    const last = runs.at(-1)
    if (
      last &&
      parseLocalDate(last.to).add({ days: 1 }).toString() === change.day &&
      sameDayRates(last.before, change.before) &&
      sameDayRates(last.after, change.after)
    ) {
      last.to = change.day
    } else {
      runs.push({ from: change.day, to: change.day, before: change.before, after: change.after })
    }
  }
  return runs
}
